// ═════════════════════════════════════════════��═════════════════
// MESSAGING: Shared types for real-time chat system
// ════════════════���═══════════════════════════════════════════��══

export type ConversationType = 'direct' | 'job' | 'system';
export type ConversationStatus = 'active' | 'archived' | 'closed';

/**
 * Message types a signed-in user may author over HTTP or the socket. `system`
 * is deliberately absent: it is reserved for server-side writers
 * (SystemMessageService, the send-system-message command) because every client
 * shows a sender-less `system` row as coming from Tabbio. The API rejects any
 * other client-supplied type with VALIDATION_ERROR (security finding M1).
 */
export const USER_MESSAGE_TYPES = ['text', 'image', 'file'] as const;
export type UserMessageType = (typeof USER_MESSAGE_TYPES)[number];
export type MessageType = UserMessageType | 'system';

export type SystemSenderIdentityKey = 'tabbio' | 'founder';

/**
 * Branded identity used for platform-authored inbox messages. It is optional on
 * transport summaries so older API responses and previously stored messages
 * continue to render with the Tabbio fallback.
 */
export interface SystemSenderIdentity {
  key: SystemSenderIdentityKey;
  name: string;
  avatarUrl: string | null;
}

export interface AdminCommunicationAttachment {
  kind: 'admin_communication';
  campaignId: string;
  senderIdentity: SystemSenderIdentity;
}

// ─── Conversation ───────────────────────────────────────────────

export interface ConversationParticipantSummary {
  id: string;
  userId: string;
  name: string;
  image?: string | null;
  unreadCount: number;
}

export interface MessageSummary {
  id: string;
  conversationId: string;
  senderId: string | null;
  content?: string | null;
  type: MessageType;
  attachments: unknown[];
  senderIdentity?: SystemSenderIdentity | null;
  readBy: ReadReceipt[];
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ReadReceipt {
  userId: string;
  readAt: string;
}

export interface ConversationSummary {
  id: string;
  type: ConversationType;
  companyId?: string | null;
  jobId?: string | null;
  applicationId?: string | null;
  lastMessageAt?: string | null;
  status: ConversationStatus;
  participants: ConversationParticipantSummary[];
  lastMessage?: MessageSummary | null;
  systemSenderIdentity?: SystemSenderIdentity | null;
  /** Current viewer's active notification mute for this conversation. */
  notificationsMuted?: boolean;
  notificationsMutedUntil?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Socket Event Payloads ──────────────────────────────────────

export interface SendMessagePayload {
  conversationId: string;
  content: string;
  /** Optional for older clients; missing means text. Never `system`. */
  type?: UserMessageType;
}

export interface NewMessageEvent {
  conversationId: string;
  message: MessageSummary;
  senderName?: string;
  senderImage?: string | null;
  senderIdentity?: SystemSenderIdentity | null;
  /** Suppresses recipient-facing toast presentation without hiding the message. */
  notificationsMuted?: boolean;
}

export interface TypingEvent {
  conversationId: string;
  userId: string;
  userName: string;
  isTyping: boolean;
}

export interface MessagesReadEvent {
  conversationId: string;
  userId: string;
  messageIds: string[];
  readAt: string;
}

export interface ConversationUpdatedEvent {
  conversation: ConversationSummary;
}

export interface UnreadCountEvent {
  conversationId: string;
  count: number;
  totalCount: number;
  // Workspace `totalCount` applies to: sidebar badges are keyed per workspace.
  workspace?: {
    mode: 'seeker' | 'employer';
    companyId: string | null;
  };
}

// Note: SOCKET_EVENTS and SYSTEM_SENDER_ID runtime constants
// are defined in index.ts to avoid Node ESM resolution issues.
// USER_MESSAGE_TYPES reaches consumers through ./messaging.runtime.js.
